import { Language } from 'types/language'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

const defaultLocale: Language = 'EN'

export type LanguageType = {
  locale: string
  displayName: string
  languageCode: Language
}

type LocaleState = {
  locale: Language
  hasHydrated: boolean
  languages: LanguageType[]
  isResourceTestMode: boolean
}
type LocaleActions = {
  setLocale: (locale: Language) => void
  setHasHydrated: (value: boolean) => void
  setLanguages: (languages: LanguageType[]) => void
  setIsResourceTestMode: (isResourceTestMode: boolean) => void

  resetLocale: () => void
}
type LocaleStore = LocaleState & LocaleActions

/**
 * Zustand 기반의 전역 locale(언어) 상태 스토어.
 *
 * @function useLocaleStore
 *
 * @description
 * 애플리케이션 전역에서 사용되는 언어(locale) 상태를 관리하는 Zustand 스토어입니다.
 * persist 미들웨어를 사용해 LocalStorage에 상태를 저장하며,
 * 새로고침 후에도 사용자가 선택한 locale 값을 유지합니다.
 * 또한 `hasHydrated` 플래그를 통해 hydration 완료 여부를 알려주므로,
 * 초기 렌더링 시 언어 불일치 문제(Flash of wrong locale)를 방지할 수 있습니다.
 *
 * @returns {Object} 상태 및 액션을 포함한 LocaleStore 객체
 * @returns {Language} return.locale - 현재 설정된 언어
 * @returns {boolean} return.hasHydrated - persist된 상태가 복원되었는지 여부
 * @returns {(locale: Language) => void} return.setLocale - 언어를 변경하는 액션
 * @returns {() => void} return.resetLocale - 언어를 기본값으로 초기화하는 액션
 * @returns {(value: boolean) => void} return.setHasHydrated - hydration 여부 설정 액션
 *
 * @example
 * const { locale, setLocale } = useLocaleStore();
 * setLocale("ko");
 *
 * @example
 * const hydrated = useLocaleStore(state => state.hasHydrated);
 * if (!hydrated) return null;
 */

export const useLocaleStore = create<LocaleStore>()(
  persist(
    (set) => ({
      locale: defaultLocale,
      hasHydrated: false,
      languages: [],
      isResourceTestMode: false,

      setLocale: (locale) => set({ locale }),
      setHasHydrated: (value) => set({ hasHydrated: value }),
      setLanguages: (languages) => set({ languages }),
      setIsResourceTestMode: (isResourceTestMode) => set({ isResourceTestMode }),
      resetLocale: () => set({ locale: defaultLocale }),
    }),
    {
      name: 'locale-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ locale: state.locale }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
